import { useEffect } from 'react';
import { useUserDetails } from '../../helpers/UserContext';
import {
  AdvancedImage,
  lazyload,
  placeholder,
  responsive,
} from '@cloudinary/react';
import { myCld } from '../../utils/constants';
import { Link } from 'react-router';
import type { GalleryImage } from '../../models/GalleryImage';
import { useQuery } from '@tanstack/react-query';
import AlpacaApi from '../../apis/alpaca_api';
import PublicProfileApi from '../../apis/publicProfileApi';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import RHFInput from '../../components/RHF/RHFInput';
import { RHFSubmitButton } from '../../components/RHF/RHFSubmitButton';
import AppLayout from '../../components/AppLayout';
import { handleError } from '../../apis';
import { toast } from 'react-toastify';
import hercsHead from '../../images/Hercules_head_cropped_300px.png';
import ShowPending from '../../components/ShowPending';

const validator_public_profile_name = z.object({
  publicProfileNameId: z.number(),
  applicationUserId: z.string(),
  profileName: z.string('Required!').trim().min(1, 'Required!'),
  allowSharing: z.boolean(),
});

export default function GalleryPage() {
  const [userDetails] = useUserDetails();

  const { data: gallery, isPending } = useQuery({
    queryKey: ['gallery'],
    queryFn: () => AlpacaApi.getGallery(),
  });

  const { data: publicProfile } = useQuery({
    queryKey: ['publicProfile'],
    queryFn: () => PublicProfileApi.getPublicProfile(),
  });

  const defaultValues = publicProfile
    ? { ...publicProfile, allowSharing: publicProfile.allowSharing === 1 }
    : {
        applicationUserId: userDetails?.userId,
        publicProfileNameId: 0,
        allowSharing: false,
      };

  const {
    handleSubmit,
    register,
    control,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: zodResolver(validator_public_profile_name),
  });

  const allowSharingValue = useWatch({ control, name: 'allowSharing' });
  const profileName = useWatch({ control, name: 'profileName' });

  useEffect(() => {
    if (publicProfile) {
      reset({
        ...publicProfile,
        allowSharing: publicProfile.allowSharing === 1,
      });
    }
  }, [publicProfile, reset]);

  const onHandleSubmit = async (
    values: z.infer<typeof validator_public_profile_name>,
  ) => {
    const data = {
      ...values,
      allowSharing: values.allowSharing ? 1 : 0,
    };
    try {
      if (!values.publicProfileNameId) {
        const res = await PublicProfileApi.setPublicProfile(data);
        if (res.data === 'Invalid Name') {
          setError('profileName', {
            message: 'This name is already taken',
          });
        }
      } else {
        await PublicProfileApi.updatePublicProfile(data);
      }
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  return (
    <AppLayout>
      <div className='w-full'>
        <h3>Share your gallery publicly?</h3>
        <form onSubmit={handleSubmit(onHandleSubmit)}>
          <div className='flex items-center flex-wrap gap-2'>
            <RHFInput
              name='profileName'
              type='text'
              label='Profile Name'
              className='min-w-[275px]'
              register={register('profileName')}
              errors={errors}
            />

            <div className={`md:mt-2 flex py-2`}>
              <div
                className={`min-w-[130px] flex justify-center items-center rounded-lg bg-gray-100 px-4 py-3 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-300`}
              >
                <label className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={allowSharingValue}
                    {...register('allowSharing')}
                    id='active'
                    className={`rounded border-gray-300 bg-gray-100 text-teal-600 focus:ring-2 focus:ring-teal-500 dark:border-gray-600 ${
                      allowSharingValue ? 'bg-teal-600 dark:bg-teal-600' : ''
                    } dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-teal-600`}
                  />
                  Make Public
                </label>
              </div>
            </div>
          </div>
          <p className='mb-2 text-sm'>
            Available @
            <a
              href={`https://alpacatracker.com/gallery/${encodeURIComponent(profileName ?? '')}`}
              className='ms-2'
            >
              https://alpacatracker.com/gallery/
              <span className='text-base text-amber-500 dark:text-amber-200'>
                {encodeURIComponent(profileName ?? '')}
              </span>
            </a>
          </p>
          <p className='mb-2 text-sm text-amber-500 dark:text-amber-200'>
            For best results, avoid punctuation and spaces. All one word or
            separate with dashes ( - ) or underscores ( _ ).
          </p>
          <p className='text-sm'>
            By electing to make public, your gallery will be visible to anyone
            with a link
            <br />
            Links to alpacas will NOT be active.
          </p>
          <RHFSubmitButton label='Submit' isSubmitting={isSubmitting} />
        </form>
        <ShowPending isPending={isPending} />
        {Array.isArray(gallery) ? (
          <div className='mt-4 grid w-full grid-cols-[repeat(auto-fit,_minmax(300px,_1fr))] justify-center gap-4'>
            {gallery.map((g: GalleryImage) => (
              <div
                key={g.alpacaImageId}
                className='image-overlay  relative flex justify-center'
              >
                <AdvancedImage
                  className='align-self-center max-h-[200px] justify-self-center rounded'
                  cldImg={myCld.image(g.imageName)}
                  plugins={[lazyload(), responsive(), placeholder()]}
                />
                <div className='image-title'>
                  {
                    <Link
                      to={`/alpacas/${g.alpacaId}`}
                      className='dark:text-gray-600'
                    >
                      {g.name}
                    </Link>
                  }
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center'>
            <h2 className='text-center'>Nothing to see here!</h2>
            <p className='text-center'>
              Uploaded images of your alpacas to be enjoyed here.
            </p>
            <div className='mt-4'>
              <img src={hercsHead} alt='Hercules' />
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
